import { UseSearchResult } from "@saleor/hooks/makeSearch";
import { maybe } from "@saleor/misc";
import {
  SearchBrands,
  SearchBrandsVariables
} from "@saleor/searches/types/SearchBrands";
import {
  SearchMalls,
  SearchMallsVariables
} from "@saleor/searches/types/SearchMalls";

import { IFilterElement } from "../../../../components/Filter";
import {
  createFilterTabUtils,
  createFilterUtils,
  dedupeFilter,
  getMultipleValueQueryParam
} from "../../../../utils/filters";
import { removeBase64Name } from "../../../utils/misc";
import {
  StoresFilterKeys,
  StoresFilterOpts
} from "../../components/StoresPage/filters";
import {
  InitialStoresReportFilterData_brands_edges_node,
  InitialStoresReportFilterData_malls_edges_node
} from "../../types/InitialStoresReportFilterData";
import {
  StoresListUrlFilters,
  StoresListUrlFiltersEnum,
  StoresListUrlFiltersWithMultipleValues,
  StoresListUrlQueryParams
} from "../../url";
export const Stores_FILTERS_KEY = "StoresFilters";

export function getFilterOpts(
  params: StoresListUrlFilters,
  brands: {
    initial: InitialStoresReportFilterData_brands_edges_node[];
    search: UseSearchResult<SearchBrands, SearchBrandsVariables>;
  },
  malls: {
    initial: InitialStoresReportFilterData_malls_edges_node[];
    search: UseSearchResult<SearchMalls, SearchMallsVariables>;
  }
): StoresFilterOpts {
  return {
    brand: {
      active: !!params.brand,
      choices: maybe(
        () =>
          brands.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.brand
        ? maybe(
            () =>
              brands.initial.map(brand => ({
                label: brand.name,
                value: brand.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => brands.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: brands.search.result.loading,
      onFetchMore: brands.search.loadMore,
      onSearchChange: brands.search.search,
      value: maybe(() => dedupeFilter(params.brand), [])
    },
    mall: {
      active: !!params.mall,
      choices: maybe(
        () =>
          malls.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.mall
        ? maybe(
            () =>
              malls.initial.map(mall => ({
                label: mall.name,
                value: mall.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => malls.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: malls.search.result.loading,
      onFetchMore: malls.search.loadMore,
      onSearchChange: malls.search.search,
      value: maybe(() => dedupeFilter(params.mall), [])
    },
    storeStatus: {
      active: !!params.storeStatus,
      value: maybe(() => dedupeFilter(params.storeStatus), [])
    }
  };
}

export function getFilterVariables(params: StoresListUrlFilters) {
  return {
    brandId: removeBase64Name(
      "Brand:",
      params.brand !== undefined ? params.brand : []
    ),
    mallId: removeBase64Name(
      "Mall:",
      params.mall !== undefined ? params.mall : []
    ),
    search: params.query,
    storeStatus: maybe(() => params.storeStatus, null)
  };
}

export function getFilterQueryParam(
  filter: IFilterElement<StoresFilterKeys>
): StoresListUrlFilters {
  const { name } = filter;

  switch (name) {
    case StoresFilterKeys.mall:
      return getMultipleValueQueryParam(
        filter,
        StoresListUrlFiltersWithMultipleValues.mall
      );
    case StoresFilterKeys.brand:
      return getMultipleValueQueryParam(
        filter,
        StoresListUrlFiltersWithMultipleValues.brand
      );
    case StoresFilterKeys.storeStatus:
      return getMultipleValueQueryParam(
        filter,
        StoresListUrlFiltersWithMultipleValues.storeStatus
      );
  }
}

export const {
  deleteFilterTab,
  getFilterTabs,
  saveFilterTab
} = createFilterTabUtils<StoresListUrlFilters>(Stores_FILTERS_KEY);

export const { areFiltersApplied, getActiveFilters } = createFilterUtils<
  StoresListUrlQueryParams,
  StoresListUrlFilters
>({
  ...StoresListUrlFiltersEnum,
  ...StoresListUrlFiltersWithMultipleValues
});
